
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Search,
  Filter,
  BookOpen,
  CheckCircle,
  Lock,
  ChevronRight,
} from "lucide-react";
import Headers from "../Header";
import Footer from "../Footer";

import ScrollToTop from "../ScrollToTopButton/ScrollToTop"

const QuestionBanksPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Mock data for question banks
  const questionBanks = [
    {
      id: 1,
      name: "JavaScript Essentials",
      description: "Core JavaScript concepts and fundamentals for web developers.",
      category: "programming",
      questionCount: 120,
      price: 49,
      popular: true,
    },
    {
      id: 2,
      name: "React Development",
      description: "React.js concepts, hooks, state management and component design.",
      category: "programming",
      questionCount: 85,
      price: 59,
    },
    {
      id: 3,
      name: "SQL Fundamentals",
      description: "Database design, SQL queries, and performance optimization.",
      category: "database",
      questionCount: 110,
      price: 39,
    },
    {
      id: 4,
      name: "Project Management",
      description: "Project planning, execution, monitoring and closing processes.",
      category: "management",
      questionCount: 150,
      price: 69,
    },
    {
      id: 5,
      name: "Data Science Fundamentals",
      description: "Statistics, Python for data analysis, and visualization techniques.",
      category: "data_science",
      questionCount: 130,
      price: 79,
    },
    {
      id: 6,
      name: "DevOps Practices",
      description: "CI/CD, containerization, cloud deployment, and infrastructure as code.",
      category: "devops",
      questionCount: 95,
      price: 69,
    },
    {
      id: 7,
      name: "UI/UX Design Principles",
      description: "User research, wireframing, prototyping, and design systems.",
      category: "design",
      questionCount: 105,
      price: 59,
    },
    {
      id: 8,
      name: "Network Security",
      description: "Security protocols, threat detection, and vulnerability assessment.",
      category: "security",
      questionCount: 125,
      price: 89,
    },
    {
      id: 9,
      name: "Digital Marketing",
      description: "SEO, social media marketing, analytics, and content strategy.",
      category: "marketing",
      questionCount: 140,
      price: 49,
    },
  ];

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "programming", label: "Programming" },
    { value: "database", label: "Database" },
    { value: "management", label: "Management" },
    { value: "data_science", label: "Data Science" },
    { value: "devops", label: "DevOps" },
    { value: "design", label: "UI/UX Design" },
    { value: "security", label: "Security" },
    { value: "marketing", label: "Marketing" },
  ];

  const filteredQuestionBanks = questionBanks.filter((bank) => {
    const matchesSearch = bank.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase()) || 
      bank.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || bank.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen">
      <Headers />
      {/* Navigation placeholder - would be replaced by your actual Navbar */}
      <div className="h-16 bg-white shadow"></div>

      {/* Hero section */}
      <section className="bg-gradient-to-b from-primary/10 to-white py-16">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Question Banks
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-gray-700 max-w-3xl mx-auto"
          >
            Browse our collection of curated question banks to enhance your
            assessment process
          </motion.p>
        </div>
      </section>

      {/* Search and filter section */}
      <section className="py-8 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between max-w-5xl mx-auto">
            <div className="relative w-full md:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search question banks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center space-x-2 w-full md:w-auto">
              <Filter className="text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-grow md:flex-grow-0 h-10 rounded-md border border-input bg-background px-3 py-2"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Question banks grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {filteredQuestionBanks.map((bank, index) => (
              <motion.div
                key={bank.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className={bank.popular ? "border-primary shadow-md" : ""}>
                  {bank.popular && (
                    <div className="bg-primary text-primary-foreground text-center py-1 px-4 text-xs font-medium">
                      POPULAR
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle>{bank.name}</CardTitle>
                      <div className="bg-primary/10 p-2 rounded-full">
                        <BookOpen className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <CardDescription>{bank.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Questions</span>
                        <span className="font-medium">{bank.questionCount}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Category</span>
                        <span className="font-medium capitalize">
                          {bank.category.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Price</span>
                        <span className="font-medium">${bank.price}</span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between items-center">
                    <div className="flex space-x-1 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Regularly updated</span>
                    </div>
                    <Button asChild>
                      <Link to={`/checkout?product=question_bank&id=${bank.id}`}>
                        View Details
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>

          {filteredQuestionBanks.length === 0 && (
            <div className="text-center py-16">
              <h3 className="text-2xl font-semibold mb-2">No results found</h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your search or filter criteria
              </p>
              <Button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Enterprise section */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow flex flex-col md:flex-row gap-8 items-center">
            <div className="md:w-2/3">
              <h2 className="text-2xl font-bold mb-4">Need Custom Question Banks?</h2>
              <p className="text-gray-700 mb-6">
                Our team can create customized question banks tailored to your
                industry, role requirements, and assessment needs.
              </p>
              <Button>
                Contact Us <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="md:w-1/3 flex justify-center">
              <div className="bg-primary/10 p-6 rounded-full">
                <Lock className="h-12 w-12 text-primary" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer placeholder */}
      <div className="bg-gray-900 text-white py-16">
          <Footer />
      </div>
      <ScrollToTop />
    </div>
  );
};

export default QuestionBanksPage;
